import { Injectable } from '@angular/core';
import { PATH_ORDERS_TAB } from '../../../../constants';
import { ORDER_TABS } from '../../../../enums';
import { OrderDTO, QuoteDTO, TripDTO } from '../../../../interfaces/models';
import { DashboardHttpService } from '../../../../modules/dashboard/services/dashboard.http.service';
import { CompanyService } from '../../../../providers/company/company.service';
import { LeadService } from '../../../../providers/leads/lead.service';
import { OrderService } from '../../../../providers/order/order.service';
import { QuoteService } from '../../../../providers/quote/quote.service';
import { ReportsService } from '../../../../providers/reports/reports.service';
import { TripService } from '../../../../providers/trip/trip.service';
import { OrderColumnComponent } from '../../custom-columns/components/order-column/order-column.component';
import { QuoteColumnComponent } from '../../custom-columns/components/quote-column/quote-column.component';
import { TableConfiguration } from '../models/table-config.model';
import { TableConfigurationBuilder } from './table-configuration.builder';
import { TableDataMapperService } from './table-data-mapper.service';

type TableFilter = Record<string, unknown>;

@Injectable()
export class TableTemplateService {

  constructor(
    private readonly leadService: LeadService,
    private readonly quoteService: QuoteService,
    private readonly companyService: CompanyService,
    private readonly tableDataMapperService: TableDataMapperService,
    private readonly tableConfigurationBuilder: TableConfigurationBuilder,
    private readonly tripService: TripService,
    private readonly orderService: OrderService,
    private readonly reportService: ReportsService,
    private readonly dashboardHttpService: DashboardHttpService,
  ) {
  }

  getQuoteLeadTabTableTemplate() {
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(({offset, limit, filter}) => this.leadService.getList({
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }, 'news'))
      .withDataMapper((quote) =>  this.tableDataMapperService.getQuoteToViewMapper(quote as QuoteDTO, {
        secondRow: {
          orderDetails: {
            orderStatus: 'Lead',
          }
        }
      }))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: QuoteColumnComponent },
      }])
      .withoutHeader()
      .config;
    return config;
  }

  getQuoteTabTableTemplate() {
    const config = TableConfiguration.create(<TableConfiguration>{
      classes: { 'configurable-table--without-thead': true },
      getDataSource: ({offset, limit, filter}) => this.leadService.getList({
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }, 'quoted'),
      mapData: (data: QuoteDTO[]) => data.map((quote) =>  this.tableDataMapperService.getQuoteToViewMapper(quote, {
        secondRow: {
          orderDetails: {
            orderStatus: 'Quoted',
          }
        }
      })),
      columns: [{
        columnName: 'location',
        componentConfig: { componentType: QuoteColumnComponent },
      }]
    });
    return config;
  }

  getQuotedTabTableTemplate() {
    const config = TableConfiguration.create(<TableConfiguration>{
      classes: { 'configurable-table--without-thead': true },
      getDataSource: ({offset, limit}) => this.quoteService.getQouteList(
        offset,
        limit,
      ),
      mapData: (data: QuoteDTO[]) => data.map((quote) => this.tableDataMapperService.getQuotedToViewMapper(quote, {
        secondRow: {
          orderDetails: {
            orderStatus: 'Active',
          }
        }
      })),
      columns: [{
        columnName: 'location',
        componentConfig: { componentType: QuoteColumnComponent },
      }]
    });
    return config;
  }

  getQuoteExpiredTabTableTemplate() {
    const config = TableConfiguration.create(<TableConfiguration>{
      classes: { 'configurable-table--without-thead': true },
      getDataSource: ({offset, limit}) => this.quoteService.getQuoteExpiredList(
        offset,
        limit,
      ),
      mapData: (data: QuoteDTO[]) => data.map((quote) => this.tableDataMapperService.getQuotedToViewMapper(quote, {
        secondRow: {
          orderDetails: {
            orderStatus: 'Expired',
          }
        }
      })),
      columns: [{
        columnName: 'location',
        componentConfig: { componentType: QuoteColumnComponent },
      }]
    });
    return config;
  }

  getOrderAvailableTabTableTemplate() {
    const config = TableConfiguration.create(<TableConfiguration>{
      classes: { 'configurable-table--without-thead': true },
      getDataSource: ({offset, limit, filter}) => this.companyService.getLoadBoard({
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }),
      mapData: (data: OrderDTO[]) => data.map((order) => this.tableDataMapperService.getLoadBoardOrderToViewMapper(order)),
      columns: [{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }]
    });
    return config;
  }

  getOrderRequestedTabTableTemplate() {
    const config = TableConfiguration.create(<TableConfiguration>{
      classes: { 'configurable-table--without-thead': true },
      getDataSource: ({offset, limit, filter}) => this.companyService.getOrders(ORDER_TABS.REQUESTED, {
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }),
      mapData: (data: OrderDTO[]) => data.map((order) => this.tableDataMapperService.getLoadBoardRequestedOrderToViewMapper(order)),
      columns: [{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }]
    });
    return config;
  }

  getOrderNewLoadsTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getNewLoadOrderToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.NEW_LOADS, mapper);
  }

  getOrderAssignedTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getAssignedAndPickedUpToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.ASSIGNED, mapper);
  }

  getOrderPickedUpTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getAssignedAndPickedUpToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.PICKED_UP,mapper);
  }

  getOrderDeliveredTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getGeneralOrderToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.DELIVERED, mapper);
  }

  getOrderClaimedTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getGeneralOrderToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.CLAIMED, mapper);
  }

  getOrderBilledTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getGeneralOrderToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.BILLED, mapper);
  }

  getOrderPastDueTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getGeneralOrderToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.PAST_DUE, mapper);
  }

  getOrderArchivedTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getGeneralOrderToViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.ARCHIVED, mapper);
  }

  getOrderPaidTabTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getPaidOrderViewMapper(order);
    return this.getOrderTabTableTemplate(PATH_ORDERS_TAB.PAID, mapper);
  }

  getTripTabTableTemplate() {
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(({offset, limit, filter}) => this.tripService.getList({
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }))
      .withDataMapper((trip) =>  this.tableDataMapperService.getTripToViewMapper(trip as TripDTO))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }])
      .withoutHeader()
      .config;
    return config;
  }

  getOrdersInTripTableTemplate(trip: TripDTO) {
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(() => this.tripService.getOrdersByTrip({}, trip.id))
      .withDataMapper((order, config: TableConfiguration) =>  this.tableDataMapperService.getOrdersInTripToViewMapper(order as OrderDTO, config))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }])
      .withContext({ tripId: trip.id })
      .withoutHeader()
      .withBar({
        header: { name: `Trip ${trip.id}`},
        actions: {
          config: {
            importOrderToTrip: {
              isDisplayed: true,
              data: {
                trip,
              }
            }
          }
        }
      })
      .config;
    return config;
  }

  getAdminPublishedTabTableTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getAdminPublishedOrderToViewMapper(order);
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.PUBLISHED, mapper);
  }
  
  getAdminRequestedTabTableTemplate() {
    const mapper = (order: OrderDTO) => this.tableDataMapperService.getAdminRequestedOrderToViewMapper(order);
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.REQUESTED, mapper);
  }
  
  getAdminAssignedTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.ASSIGNED);
  }

  getAdminPickupTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.PICKED_UP);
  }

  getAdminDeliveredTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.DELIVERED);
  }

  getAdminClaimedTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.CLAIMED);
  }

  getAdminDispatchedTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.DISPATCHED);
  }
  
  getAdminDeclinedTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.EXPIRED);
  }

  getAdminPaidTabTableTemplate() {
    return this.getAdminOrdersTabTableTemplate(ORDER_TABS.PAID);
  }

  getReportOrdersTabTableTemplate() {
    const path = this.reportService.getCustomOrderPaths();
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(({offset, limit, filter}) => this.companyService.getReportOrders(path, {
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }))
      .withDataMapper(order => this.tableDataMapperService.getReportOrderToViewMapper(order as OrderDTO))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }])
      .withoutHeader()
      .config;
    return config;
  }

  getDashboardPastDueOrdersTabTableTemplate() {
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(({offset, limit}) => this.dashboardHttpService.getPastDueOrders({
        offset,
        limit,
      }))
      .withDataMapper(order => this.tableDataMapperService.getDashboardOrderToViewMapper(order as OrderDTO))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }])
      .withoutHeader()
      .config;
    return config;
  }

  private getAdminOrdersTabTableTemplate(status: string, mapper?: (order: OrderDTO) => unknown) {
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(({offset, limit, filter}) => this.orderService.getListOrders(status, {
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }))
      .withDataMapper(order => mapper && mapper(order as OrderDTO) || this.tableDataMapperService.getAdminOrderToViewMapper(order as OrderDTO))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }])
      .withoutHeader()
      .config;
    return config;
  }

  private getOrderTabTableTemplate(status: string, mapper?: (order: OrderDTO) => unknown) {
    const config = this.tableConfigurationBuilder
      .create()
      .withDataSource(({offset, limit, filter}) => this.companyService.getOrdersByStatus({
        offset,
        limit,
        ...((filter || {}) as TableFilter),
      }, status))
      .withDataMapper(order => mapper && mapper(order as OrderDTO) || this.tableDataMapperService.getOrderToViewMapper(order as OrderDTO))
      .withCounterDataSource(this.companyService.getOrdersByStatus({ onlyCount: true }, status))
      .withColumns([{
        columnName: 'location',
        componentConfig: { componentType: OrderColumnComponent },
      }])
      .withoutHeader()
      .config;
    return config;
  }
}
